// Texto assentado nas linhas da folha (texto 09, G3 e G4).
// Cada tamanho de letra ocupa um número inteiro de linhas da folha (40 unidades), então a base das letras cai sempre
// na linha da pautada ou na grade da quadriculada e da pontilhada. Os textos antigos (tamanhos 32/40/56/76 ou os do
// A−/A+) são lidos pelo nível mais próximo (Pequena · Normal · Grande · Enorme).
// 0.72 (texto 11b B5 / 11c B10): tamanho livre (barrinha) e estilo por TRECHO (tamanho, negrito, itálico, sublinhado e
// cor só do trecho selecionado). Textos sem "trechos" continuam exatamente como eram.
import type { Folha, QuadroTexto, TrechoTexto } from '../store/types';
import { N_ } from './i18n';

/** Distância entre as linhas da folha (mesma do FolhaFundo). */
export const PASSO_FOLHA = 40;

export type NivelTexto = { k: 'p' | 'n' | 'g' | 'e'; nome: string; tam: number; linha: number };
export const NIVEIS_TEXTO: NivelTexto[] = [
  { k: 'p', nome: N_('Pequena'), tam: 26, linha: PASSO_FOLHA },        // mesma linha da Normal, letra menor
  { k: 'n', nome: N_('Normal'), tam: 34, linha: PASSO_FOLHA },
  { k: 'g', nome: N_('Grande'), tam: 52, linha: PASSO_FOLHA * 2 },
  { k: 'e', nome: N_('Enorme'), tam: 76, linha: PASSO_FOLHA * 3 },
];
export const TAM_NORMAL = NIVEIS_TEXTO[1].tam;
/** Barrinha do tamanho da letra (na escala do quadro). O número mostrado na barra é a metade (Normal = 17). */
export const TAM_MIN = 20, TAM_MAX = 120;
export const numeroDoTam = (tam: number) => Math.round(tam / 2);

/** Nível (0 a 3) de um tamanho guardado. Sem tamanho = Normal. Aceita os valores antigos (32, 40, 56, 76...). */
export function nivelDoTexto(tam?: number | null): number {
  const t = tam ?? 40;
  return t <= 32 ? 0 : t <= 46 ? 1 : t <= 64 ? 2 : 3;
}

/** Tamanho da letra da caixa (sem os trechos). */
export function tamDoTexto(x: Pick<QuadroTexto, 'tam' | 'titulo' | 'tamLivre'>): number {
  if (x.titulo) return x.tam ?? 64;
  if (x.tamLivre) return Math.max(TAM_MIN, Math.min(TAM_MAX, x.tam ?? TAM_NORMAL));
  return NIVEIS_TEXTO[nivelDoTexto(x.tam)].tam;
}

/** Altura da linha para uma letra: sempre um número inteiro de linhas da folha (26 e 34 → 1, 52 → 2, 76 → 3). */
export const linhaParaLetra = (f: number) => Math.max(1, Math.ceil((f * 1.19 - 2) / PASSO_FOLHA)) * PASSO_FOLHA;

/** Tamanho da letra (f), altura de cada linha (l) e a maior letra do texto (fm, contando os trechos), na escala do quadro.
 *  O "Título" fica como era. */
export function medidasTexto(x: Pick<QuadroTexto, 'tam' | 'titulo' | 'tamLivre' | 'trechos'>): { f: number; l: number; fm: number } {
  const f = tamDoTexto(x);
  const fm = Math.max(f, ...(x.trechos ?? []).map(t => t.tam ?? 0));
  if (x.titulo) return { f, l: Math.round(fm * 1.35), fm };
  return { f, l: linhaParaLetra(fm), fm };
}

/** Do topo da linha do texto até a base das letras. Vale no iPhone e no Android (os dois centralizam a letra na
 *  altura da linha quando ela é maior que a letra; quando não cabe, a letra encosta embaixo). Diferença < 1 pt. */
export function baseDaLinha(f: number, l: number): number {
  const natural = 1.19 * f;
  return l >= natural ? l / 2 + 0.36 * f : l - 0.24 * f;
}

/** Onde ficam as linhas de cada tipo de folha: a base cai em off + 40·k, a partir de min. */
function linhasDaFolha(folha: Folha): { off: number; min: number } | null {
  const tp = folha.topo;   // texto 20 (3): espaço antes da 1ª linha nas páginas novas
  switch (folha.tipo) {
    case 'pautada': case 'margem': return { off: 39, min: (tp ?? 80) + 39 };   // linhas começam abaixo do cabeçalho (y 80)
    case 'quadriculada': return { off: 0, min: (tp ?? 0) + PASSO_FOLHA };
    case 'pontilhada': return { off: PASSO_FOLHA / 2, min: (tp ?? 0) + PASSO_FOLHA / 2 };
    default: return null;
  }
}

/** Topo do texto já assentado na linha mais próxima (folha lisa e "Título" ficam onde estão). */
export function yAssentado(x: Pick<QuadroTexto, 'y' | 'tam' | 'titulo' | 'tamLivre' | 'trechos'>, folha?: Folha | null): number {
  if (x.titulo || !folha) return x.y;
  const g = linhasDaFolha(folha); if (!g) return x.y;
  const { fm, l } = medidasTexto(x);
  const b = baseDaLinha(fm, l);
  let base = Math.round((x.y + b - g.off) / PASSO_FOLHA) * PASSO_FOLHA + g.off;
  if (base < g.min) base = g.min;
  while (base - b < 0) base += PASSO_FOLHA;
  return Math.round((base - b) * 10) / 10;
}

// ─── Estilo por trecho (0.72) ─────────────────────────────────────────────────────────────────
export type EstiloTrecho = Omit<TrechoTexto, 'a' | 'z'>;
const CHAVES: (keyof EstiloTrecho)[] = ['tam', 'b', 'i', 'u', 'cor'];
const soEstilo = (t: Partial<TrechoTexto>): EstiloTrecho => {
  const e: EstiloTrecho = {};
  for (const k of CHAVES) if (t[k] !== undefined) (e as any)[k] = t[k];
  return e;
};
const iguais = (a: EstiloTrecho, b: EstiloTrecho) => CHAVES.every(k => a[k] === b[k]);

/** Trechos em ordem, sem sobrepor, sem vazios e com os vizinhos iguais juntados. */
function normalizar(tr: TrechoTexto[], len: number): TrechoTexto[] {
  const out: TrechoTexto[] = [];
  for (const t of [...tr].sort((p, q) => p.a - q.a)) {
    const a = Math.max(0, t.a), z = Math.min(len, t.z);
    if (z <= a) continue;
    const e = soEstilo(t); if (!Object.keys(e).length) continue;
    const u = out[out.length - 1];
    if (u && u.z === a && iguais(soEstilo(u), e)) u.z = z; else out.push({ ...e, a, z });
  }
  return out;
}

/** Aplica o estilo (patch) só ao trecho [a, z) do texto (len = tamanho do texto). */
export function aplicarEstilo(trechos: TrechoTexto[] | undefined, len: number, a: number, z: number, patch: EstiloTrecho): TrechoTexto[] {
  const tr = trechos ?? [];
  if (z <= a) return tr;
  const pontos = new Set<number>([0, len, a, z]);
  for (const t of tr) { pontos.add(t.a); pontos.add(t.z); }
  const ord = [...pontos].filter(p => p >= 0 && p <= len).sort((p, q) => p - q);
  const res: TrechoTexto[] = [];
  for (let i = 0; i + 1 < ord.length; i++) {
    const s0 = ord[i], s1 = ord[i + 1];
    const base = tr.find(t => t.a <= s0 && t.z >= s1);
    let e: EstiloTrecho = base ? soEstilo(base) : {};
    if (s0 >= a && s1 <= z) e = { ...e, ...patch };
    res.push({ ...e, a: s0, z: s1 });
  }
  return normalizar(res, len);
}

/** Ajusta os trechos depois de digitar ou apagar (o texto novo herda o estilo do trecho onde foi escrito).
 *  pendente: estilo escolhido sem seleção, que vale para o que for digitado a seguir. */
export function ajustarTrechos(antigo: string, novo: string, trechos: TrechoTexto[] | undefined, pendente?: EstiloTrecho | null): TrechoTexto[] | undefined {
  if (!trechos?.length && !(pendente && Object.keys(pendente).length)) return trechos;
  let p = 0;
  while (p < antigo.length && p < novo.length && antigo[p] === novo[p]) p++;
  let s = 0;
  while (s < antigo.length - p && s < novo.length - p && antigo[antigo.length - 1 - s] === novo[novo.length - 1 - s]) s++;
  const fimAnt = antigo.length - s, ins = novo.length - p - s, delta = novo.length - antigo.length;
  // começo de trecho: escrever logo antes dele fica fora dele; fim de trecho: escrever logo depois continua no estilo dele
  const mapaA = (a: number) => (a < p ? a : a >= fimAnt ? a + delta : a === p ? p : p + ins);
  const mapaZ = (z: number) => (z < p ? z : z >= fimAnt ? z + delta : z === p ? p : p + ins);
  let tr: TrechoTexto[] = (trechos ?? []).map(t => ({ ...t, a: mapaA(t.a), z: mapaZ(t.z) }));
  if (pendente && ins > 0 && Object.keys(pendente).length) tr = aplicarEstilo(tr, novo.length, p, p + ins, pendente);
  const n = normalizar(tr, novo.length);
  return n.length ? n : undefined;
}

/** Estilo de um ponto do texto: o da caixa com o do trecho por cima. */
export function estiloNoPonto(x: QuadroTexto, pos: number): Required<Pick<EstiloTrecho, 'tam' | 'b' | 'i' | 'u'>> & { cor?: string } {
  const t = (x.trechos ?? []).find(y => y.a <= pos && pos < y.z) ?? (pos > 0 ? (x.trechos ?? []).find(y => y.a < pos && pos === y.z) : undefined);
  return { tam: t?.tam ?? tamDoTexto(x), b: t?.b ?? !!x.b, i: t?.i ?? !!x.i, u: t?.u ?? !!x.u, cor: t?.cor ?? x.cor };
}

export type Segmento = { texto: string; a: number; e: EstiloTrecho };
/** O texto em pedaços, cada um com o estilo próprio do trecho (vazio = estilo da caixa). */
export function segmentos(x: Pick<QuadroTexto, 'texto' | 'trechos'>): Segmento[] {
  const tr = normalizar(x.trechos ?? [], x.texto.length);
  if (!tr.length) return [{ texto: x.texto, a: 0, e: {} }];
  const out: Segmento[] = [];
  let pos = 0;
  for (const t of tr) {
    if (t.a > pos) out.push({ texto: x.texto.slice(pos, t.a), a: pos, e: {} });
    out.push({ texto: x.texto.slice(t.a, t.z), a: t.a, e: soEstilo(t) });
    pos = t.z;
  }
  if (pos < x.texto.length) out.push({ texto: x.texto.slice(pos), a: pos, e: {} });
  return out;
}

// ─── Palavra no ponto tocado (texto 11c B10) ──────────────────────────────────────────────────
const LETRA = /[A-Za-z0-9À-ÖØ-öø-ÿĀ-ſ_'’-]/;
/** Início e fim da palavra na posição i (sem palavra: os dois em i). */
export function palavraEm(texto: string, i: number): { start: number; end: number } {
  let k = Math.max(0, Math.min(texto.length, i));
  if (k === texto.length || !LETRA.test(texto[k])) { if (k > 0 && LETRA.test(texto[k - 1])) k--; else return { start: i, end: i }; }
  let a = k, z = k;
  while (a > 0 && LETRA.test(texto[a - 1])) a--;
  while (z < texto.length && LETRA.test(texto[z])) z++;
  return { start: a, end: z };
}
/** Largura aproximada de cada letra (em "em"), só para achar o ponto tocado numa linha medida. */
export function larguraLetra(c: string): number {
  if (/[iljI.,:;'|!]/.test(c)) return 0.28;
  if (/[ftr ()\[\]]/.test(c)) return 0.36;
  if (/[mwMW@]/.test(c)) return 0.86;
  if (/[A-Z0-9#%&]/.test(c)) return 0.66;
  return 0.54;
}
